import { inject, Injectable } from "@angular/core";
import { HttpBackend, HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, fromEvent, Observable, of, Subscription, timer } from "rxjs";
import { catchError, map, switchMap, tap, timeout } from "rxjs/operators";
import { MITPService } from "./mitp.service";
import { MaintenanceService } from "./maintenance.service";
import { Maintenance } from "../interfaces/maintenance";

export enum EConnectivityStatus {
	online = "online",
	slow = "slow",
	offline = "offline",
}

/**
 * Reports the real reachability of the backend rather than trusting `navigator.onLine`,
 * which only tells whether a network interface is up (a captive portal or a dead upstream
 * still reports `true`). The status is refreshed by polling a lightweight endpoint and
 * classifying its round-trip time.
 *
 * The same probe tells `MaintenanceService` whether the platform is in maintenance: `/status`
 * stays open during one, and is how the webapp learns that it started or ended.
 */
@Injectable({
	providedIn: "root",
})
export class ConnectivityService {
	/** Probe cadence while the connection is healthy. */
	private static readonly HEALTHY_INTERVAL_MS = 30_000;
	/** Probe cadence while degraded, so a recovery is picked up quickly. */
	private static readonly DEGRADED_INTERVAL_MS = 5_000;
	/** Above this round-trip time the connection is reported as slow. */
	private static readonly SLOW_LATENCY_MS = 2_000;
	/** Past this round-trip time the probe is abandoned and the upstream considered unreachable. */
	private static readonly PROBE_TIMEOUT_MS = 8_000;
	/** Consecutive failed probes required before reporting offline, so a single blip does not flap the banner. */
	private static readonly FAILURES_BEFORE_OFFLINE = 2;

	private mitp = inject(MITPService);
	private maintenance = inject(MaintenanceService);
	/** Built on `HttpBackend` so probes bypass the interceptor: no spinner, no error dialog, no logout on 401. */
	private httpClient = new HttpClient(inject(HttpBackend));
	private statusSubject = new BehaviorSubject<EConnectivityStatus>(EConnectivityStatus.online);
	status$: Observable<EConnectivityStatus> = this.statusSubject.asObservable();

	private probeSubscription?: Subscription;
	private browserEventsSubscription?: Subscription;
	private consecutiveFailures = 0;

	get status(): EConnectivityStatus {
		return this.statusSubject.value;
	}

	start(): void {
		if (this.probeSubscription) {
			return;
		}
		this.browserEventsSubscription = new Subscription();
		this.browserEventsSubscription.add(
			fromEvent(window, "offline").subscribe(() => {
				this.consecutiveFailures = ConnectivityService.FAILURES_BEFORE_OFFLINE;
				this.setStatus(EConnectivityStatus.offline);
			})
		);
		// The interface came back, or the tab woke up: re-check now instead of waiting for the next tick.
		this.browserEventsSubscription.add(fromEvent(window, "online").subscribe(() => this.scheduleProbe(0)));
		this.browserEventsSubscription.add(
			fromEvent(document, "visibilitychange").subscribe(() => {
				if (!document.hidden) {
					this.scheduleProbe(0);
				}
			})
		);
		this.scheduleProbe(0);
	}

	stop(): void {
		this.probeSubscription?.unsubscribe();
		this.probeSubscription = undefined;
		this.browserEventsSubscription?.unsubscribe();
		this.browserEventsSubscription = undefined;
	}

	private scheduleProbe(delayMs: number): void {
		this.probeSubscription?.unsubscribe();
		this.probeSubscription = timer(delayMs)
			.pipe(switchMap(() => this.probe()))
			.subscribe((status) => {
				this.setStatus(status);
				this.scheduleProbe(
					status === EConnectivityStatus.online
						? ConnectivityService.HEALTHY_INTERVAL_MS
						: ConnectivityService.DEGRADED_INTERVAL_MS
				);
			});
	}

	private probe(): Observable<EConnectivityStatus> {
		if (!navigator.onLine) {
			this.consecutiveFailures = ConnectivityService.FAILURES_BEFORE_OFFLINE;
			return of(EConnectivityStatus.offline);
		}
		const startedAt = performance.now();
		// Cache-busted so a proxy or the browser cache cannot answer for a dead upstream.
		return this.httpClient
			.get<{ maintenance?: Maintenance }>(`${this.mitp.Status()}?connectivity=${Date.now()}`, {
				observe: "response",
			})
			.pipe(
				timeout(ConnectivityService.PROBE_TIMEOUT_MS),
				tap((response) => this.maintenance.update(response.body?.maintenance ?? null)),
				map(() => this.onProbeReached(startedAt)),
				catchError((error: unknown) => of(this.onProbeFailed(error, startedAt)))
			);
	}

	private onProbeReached(startedAt: number): EConnectivityStatus {
		this.consecutiveFailures = 0;
		return performance.now() - startedAt > ConnectivityService.SLOW_LATENCY_MS
			? EConnectivityStatus.slow
			: EConnectivityStatus.online;
	}

	private onProbeFailed(error: unknown, startedAt: number): EConnectivityStatus {
		// Any HTTP status means the upstream answered: 401/403/404/500 all prove the path is alive.
		if (error instanceof HttpErrorResponse && error.status > 0) {
			return this.onProbeReached(startedAt);
		}
		this.consecutiveFailures++;
		if (this.consecutiveFailures >= ConnectivityService.FAILURES_BEFORE_OFFLINE) {
			return EConnectivityStatus.offline;
		}
		// First failure: degraded, but not confirmed dead yet.
		return this.status === EConnectivityStatus.offline ? EConnectivityStatus.offline : EConnectivityStatus.slow;
	}

	private setStatus(status: EConnectivityStatus): void {
		if (this.statusSubject.value !== status) {
			this.statusSubject.next(status);
		}
	}
}
